/**
 * Thin fetch wrapper around the TIRTA SMART REST API.
 * - Same-origin (Vite proxies /api to the Fastify server in dev).
 * - Cookie-based sessions with double-submit CSRF for mutating requests.
 */
const API_BASE = import.meta.env.VITE_API_BASE ?? '/api/v1';

export interface ApiErrorPayload {
  code: string;
  message: string;
  details?: Array<{ path: string; message: string }> | unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: ApiErrorPayload['details'];

  constructor(status: number, payload: ApiErrorPayload) {
    super(payload.message || 'Terjadi kesalahan.');
    this.name = 'ApiError';
    this.status = status;
    this.code = payload.code;
    this.details = payload.details;
  }
}

export function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { accept: 'application/json', ...options.headers };

  if (options.body !== undefined) {
    headers['content-type'] = 'application/json';
  }
  if (method !== 'GET' && method !== 'HEAD') {
    const csrf = readCookie('tirta_csrf');
    if (csrf) headers['x-csrf-token'] = csrf;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    method,
    credentials: 'include',
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
    signal: options.signal,
  });

  const text = await response.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }

  if (!response.ok) {
    const errorPayload =
      parsed && typeof parsed === 'object' && 'error' in parsed
        ? (parsed as { error: ApiErrorPayload }).error
        : { code: 'UNKNOWN', message: `Permintaan gagal (${response.status}).` };
    throw new ApiError(response.status, errorPayload);
  }

  return parsed as T;
}

export interface Paginated<T> {
  data: T[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>(path, { signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  upload: async <T>(path: string, file: File, fields?: Record<string, string>): Promise<T> => {
    const form = new FormData();
    // Field non-berkas dikirim lebih dulu agar tersedia saat parsing multipart.
    for (const [key, value] of Object.entries(fields ?? {})) form.append(key, value);
    form.append('file', file);
    const csrf = readCookie('tirta_csrf');
    const response = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: csrf ? { 'x-csrf-token': csrf } : undefined,
      body: form,
    });
    const text = await response.text();
    const parsed = text ? JSON.parse(text) : null;
    if (!response.ok) {
      const errorPayload =
        parsed && typeof parsed === 'object' && 'error' in parsed
          ? (parsed as { error: ApiErrorPayload }).error
          : { code: 'UNKNOWN', message: 'Unggahan gagal.' };
      throw new ApiError(response.status, errorPayload);
    }
    return parsed as T;
  },
};

/** Absolute-or-relative URL for a stored file (served by the API). */
export function fileUrl(id?: string | null): string | null {
  return id ? `${API_BASE}/files/${id}` : null;
}

/** Fetch a binary resource (e.g. a generated PDF) with the session cookie. */
export async function fetchBlob(path: string): Promise<Blob> {
  const response = await fetch(`${API_BASE}${path}`, { credentials: 'include' });
  if (!response.ok) {
    let errorPayload: ApiErrorPayload | undefined;
    try {
      const parsed = (await response.json()) as { error?: ApiErrorPayload };
      errorPayload = parsed.error;
    } catch {
      errorPayload = undefined;
    }
    throw new ApiError(
      response.status,
      errorPayload ?? { code: 'UNKNOWN', message: 'Gagal mengunduh berkas.' },
    );
  }
  return response.blob();
}

/** Build a querystring from a plain object, dropping empty values. */
export function toQuery(
  params: Record<string, string | number | boolean | undefined | null>,
): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}
